import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GlgButton, GlgTooltip } from 'Shared/components'

import useSessionSaver from '../../hooks/useSessionSaver'
import useCloningStepsActions from '../../hooks/useCloningStepsActions'
import { selectCloningModal, selectCloningStep, setCloningModal, selectSessionStarting } from '../../reducers/cloning_steps'
import { selectIsSeasonSelected } from '../../reducers/leagues_configs'
import { selectAnyLeagueHasEmptyRename } from '../../reducers/session_saver'
import { CloningStepsEnum, CloningStepsModals } from '../../constants'
import MissingRenamesModal from './modals/MissingRenamesModal'
import CancelSessionModal from './modals/CancelSessionModal'
import SaveDraftSuccessModal from './modals/SaveDraftSuccessModal'
import SaveDraftErrorModal from './modals/SaveDraftErrorModal'
import OutdatedLeaguesModal from './modals/OutdatedLeaguesModal'
import SessionInProgressModal from './modals/SessionInProgressModal'
import ConfirmRoundRemovalModal from './modals/ConfirmRoundRemovalModal'

const ModalsMapping = {
  [CloningStepsModals.MISSING_LEAGUES_RENAMES]: <MissingRenamesModal/>,
  [CloningStepsModals.CANCEL_SESSION]: <CancelSessionModal/>,
  [CloningStepsModals.SAVE_DRAFT_SUCCESS]: <SaveDraftSuccessModal/>,
  [CloningStepsModals.SAVE_DRAFT_ERROR]: <SaveDraftErrorModal/>,
  [CloningStepsModals.OUTDATED_LEAGUES]: <OutdatedLeaguesModal/>,
  [CloningStepsModals.SESSION_IN_PROGRESS]: <SessionInProgressModal/>,
  [CloningStepsModals.CONFIRM_ROUND_REMOVAL]: <ConfirmRoundRemovalModal/>,
}

const Footer = () => {
  const dispatch = useDispatch()
  const cloningModal = useSelector(selectCloningModal)
  const cloningStep = useSelector(selectCloningStep)
  const isSeasonSelected = useSelector(selectIsSeasonSelected)
  const anyLeagueHasEmptyRename = useSelector(selectAnyLeagueHasEmptyRename)
  const sessionStarting = useSelector(selectSessionStarting)
  const { saveSessionToServer, isLoading, error } = useSessionSaver()
  const { goToNextCloningStep, goToPrevCloningStep, isLastCloningStep, isNextStepAllowed } = useCloningStepsActions()

  const [ isSavingDraft, setIsSavingDraft ] = useState(false)
  const prevIsLoadingRef = useRef(false)

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && isSavingDraft) {
      setIsSavingDraft(false)
      if (!error) {
        dispatch(setCloningModal(CloningStepsModals.SAVE_DRAFT_SUCCESS))
      } else {
        dispatch(setCloningModal(CloningStepsModals.SAVE_DRAFT_ERROR))
      }
    }
    prevIsLoadingRef.current = isLoading
  }, [ isLoading, isSavingDraft, error, dispatch ])

  const handleSaveAsDraft = () => {
    setIsSavingDraft(true)
    saveSessionToServer()
  }

  const continueButton = (
    <GlgButton
      text={isLastCloningStep ? window.I18n.t('buttons.finish') : window.I18n.t('buttons.continue') }
      className={'cls-footer-button'}
      disabled={!isNextStepAllowed || isSavingDraft || sessionStarting}
      onClick={goToNextCloningStep}
    />
  )

  const stepTooltips = useMemo(() => ({
    [CloningStepsEnum.RENAME]: anyLeagueHasEmptyRename
      ? window.I18n.t('clone_leagues_sessions.errors.empty_league_name')
      : null,
    [CloningStepsEnum.CONFIG]: !isSeasonSelected
      ? window.I18n.t('clone_leagues_sessions.footer.continue_tooltip_season_required')
      : null,
  }), [ anyLeagueHasEmptyRename, isSeasonSelected ])

  const stepTooltip = stepTooltips[cloningStep]

  const wrapWithTooltip = (button) => {
    if (!stepTooltip) {
      return button
    }

    return <GlgTooltip tooltip={stepTooltip}><span>{button}</span></GlgTooltip>
  }

  return <div className={'cls-footer'}>
    <GlgButton
      text={window.I18n.t('buttons.back')}
      className={'cls-footer-button-dark'}
      disabled={isSavingDraft || sessionStarting}
      onClick={goToPrevCloningStep}
    />
    {wrapWithTooltip(
      <GlgButton
        text={window.I18n.t('buttons.save_as_draft')}
        className={'cls-footer-button-dark'}
        disabled={!!stepTooltip || isSavingDraft || sessionStarting}
        onClick={handleSaveAsDraft}
      />
    )}
    {wrapWithTooltip(continueButton)}
    {cloningModal && ModalsMapping[cloningModal]}
  </div>
}

export default Footer
